class PixelatedEffect {
  constructor(options = {}) {
    this.config = {
      selector: '.pixelated-effect__wrapper',
      loadedClass: 'loaded',
      errorClass: 'load-failed',
      ...options
    };

    this.processedElements = new WeakSet();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.scanAndApply(), { once: true });
    } else {
      this.scanAndApply();
    }

    this.setupMutationObserver();
  }

  setupMutationObserver() {
    if (!('MutationObserver' in window)) return;

    const observer = new MutationObserver((mutations) => {
      let hasNewNodes = false;

      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          hasNewNodes = true;
          break;
        }
      }

      if (hasNewNodes) {
        // Batch DOM reads with requestAnimationFrame
        requestAnimationFrame(() => this.scanAndApply());
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  scanAndApply() {
    const elements = document.querySelectorAll(this.config.selector);

    for (const element of elements) {
      if (!this.processedElements.has(element)) {
        this.processedElements.add(element);
        this.processElement(element);
      }
    }
  }

  processElement(wrapper) {
    const mainImg = wrapper.querySelector('img:not(.pixelated-effect__img-container img)');

    if (!mainImg) return;

    // Check if already loaded
    if (this.isImageLoaded(mainImg)) {
      wrapper.classList.add(this.config.loadedClass);
      return;
    }

    // Use single event listener with proper cleanup
    const onLoad = () => {
      wrapper.classList.add(this.config.loadedClass);
      cleanup();
    };

    const onError = () => {
      wrapper.classList.add(this.config.errorClass);
      cleanup();
    };

    const cleanup = () => {
      mainImg.removeEventListener('load', onLoad);
      mainImg.removeEventListener('error', onError);
    };

    mainImg.addEventListener('load', onLoad);
    mainImg.addEventListener('error', onError);
  }

  isImageLoaded(img) {
    return img.complete && img.naturalHeight > 0;
  }

  refresh() {
    this.scanAndApply();
  }

  static create(options = {}) {
    return new PixelatedEffect(options);
  }
}

// Initialize with production settings (logging disabled)
const pixelatedEffect = PixelatedEffect.create();

if (typeof window !== 'undefined') {
  window.PixelatedEffect = PixelatedEffect;
  window.pixelatedEffect = pixelatedEffect;
}
